import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Tabs,
  Tab,
  IconButton,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  Inventory as InventoryIcon,
  Build as WorkshopIcon,
  ShoppingCart as SalesIcon
} from '@mui/icons-material';
import { AuthContext } from '../../contexts/AuthContext';
import { db } from '../../firebase';
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { format, startOfMonth, endOfMonth, subMonths, parseISO } from 'date-fns';
import { pl } from 'date-fns/locale';

// Import komponentów do wykresów (zainstalowanych wcześniej)
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from 'chart.js';
import { Pie, Bar, Line } from 'react-chartjs-2';

// Rejestracja niezbędnych komponentów Chart.js
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

const Dashboard = () => {
  const { currentUser } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [employees, setEmployees] = useState([]);
  
  // Dane statystyczne
  const [stats, setStats] = useState({
    totalOrders: 0,
    pendingOrders: 0,
    completedOrders: 0,
    totalDeposits: 0,
    totalSales: 0,
    workshopItems: 0,
    approvedSalesCount: 0,
    pendingSalesCount: 0,
    rejectedSalesCount: 0,
    approvedSalesValue: 0,
    pendingSalesValue: 0
  });

  // Stan do przechowywania wszystkich danych
  const [ordersData, setOrdersData] = useState([]);
  const [workshopData, setWorkshopData] = useState([]);
  const [productsData, setProductsData] = useState([]);
  const [salesData, setSalesData] = useState([]);

  const isAdmin = currentUser?.role === 'admin';
  
  // Funkcja do pobierania danych pracowników (tylko dla administratora)
  const fetchEmployees = async () => {
    if (!isAdmin) return;
    
    try {
      const employeesCollection = collection(db, 'users');
      const employeesSnapshot = await getDocs(employeesCollection);
      const employeesList = employeesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setEmployees(employeesList);
    } catch (err) {
      console.error('Błąd podczas pobierania pracowników:', err);
      setError('Nie udało się pobrać listy pracowników');
    }
  };

  // Funkcja zmiany zakładki
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Funkcja zmiany miesiąca
  const handleMonthChange = (event) => {
    setSelectedMonth(parseISO(event.target.value));
  };

  // Funkcja zmiany pracownika
  const handleEmployeeChange = (event) => {
    setSelectedEmployee(event.target.value);
  };

  // Funkcja do odświeżania danych
  const handleRefresh = () => {
    fetchDashboardData();
  };

  // Funkcja pobierająca wszystkie dane do dashboardu
  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Tworzymy zakres dat dla wybranego miesiąca
      const startDate = startOfMonth(selectedMonth);
      const endDate = endOfMonth(selectedMonth);
      
      // Pobieranie zamówień
      await fetchOrdersData(startDate, endDate);
      
      // Pobieranie danych o pracowni
      await fetchWorkshopData();
      
      // Pobieranie danych o produktach
      await fetchProductsData();
      
      // Pobieranie danych o sprzedaży
      await fetchSalesData(startDate, endDate);
      
      setLoading(false);
    } catch (err) {
      console.error('Błąd podczas pobierania danych dashboardu:', err);
      setError('Nie udało się pobrać danych statystycznych');
      setLoading(false);
    }
  };
  
  // Funkcja do pobierania zamówień
  const fetchOrdersData = async (startDate, endDate) => {
    try {
      const ordersCollection = collection(db, 'orders');
      let ordersQuery;
      
      // Filtrowanie według rol i wybranego pracownika
      if (isAdmin && selectedEmployee !== 'all') {
        ordersQuery = query(
          ordersCollection,
          where('created_at', '>=', Timestamp.fromDate(startDate)),
          where('created_at', '<=', Timestamp.fromDate(endDate)),
          where('employee_id', '==', selectedEmployee)
        );
      } else if (isAdmin) {
        ordersQuery = query(
          ordersCollection,
          where('created_at', '>=', Timestamp.fromDate(startDate)),
          where('created_at', '<=', Timestamp.fromDate(endDate))
        );
      } else {
        // Zwykły użytkownik widzi tylko swoje zamówienia
        ordersQuery = query(
          ordersCollection,
          where('created_at', '>=', Timestamp.fromDate(startDate)),
          where('created_at', '<=', Timestamp.fromDate(endDate)),
          where('employee_id', '==', currentUser.uid)
        );
      }
      
      const ordersSnapshot = await getDocs(ordersQuery);
      const ordersList = ordersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        created_at: doc.data().created_at?.toDate() || new Date(),
        estimated_completion: doc.data().estimated_completion?.toDate() || new Date()
      }));
      
      setOrdersData(ordersList);
      
      // Obliczanie statystyk zamówień
      const totalOrders = ordersList.length;
      const pendingOrders = ordersList.filter(order => ['new', 'in_progress'].includes(order.status)).length;
      const completedOrders = ordersList.filter(order => order.status === 'ready').length;
      
      // Obliczanie sumy zadatków i sprzedaży
      const totalDeposits = ordersList
        .filter(order => order.has_deposit && order.deposit_amount)
        .reduce((sum, order) => sum + (parseFloat(order.deposit_amount) || 0), 0);
      
      const totalSales = ordersList
        .filter(order => order.status === 'ready')
        .reduce((sum, order) => sum + (parseFloat(order.total_amount) || 0), 0);
      
      setStats(prevStats => ({
        ...prevStats,
        totalOrders,
        pendingOrders,
        completedOrders,
        totalDeposits,
        totalSales
      }));
    } catch (err) {
      console.error('Błąd podczas pobierania zamówień:', err);
    }
  };
  
  // Funkcja do pobierania danych pracowni
  const fetchWorkshopData = async () => {
    try {
      const workshopCollection = collection(db, 'workshop');
      let workshopQuery;
      
      if (isAdmin && selectedEmployee !== 'all') {
        workshopQuery = query(
          workshopCollection,
          where('employee_id', '==', selectedEmployee),
          where('status', 'in', ['waiting', 'in_progress'])
        );
      } else if (isAdmin) {
        workshopQuery = query(
          workshopCollection,
          where('status', 'in', ['waiting', 'in_progress'])
        );
      } else {
        workshopQuery = query(
          workshopCollection,
          where('employee_id', '==', currentUser.uid),
          where('status', 'in', ['waiting', 'in_progress'])
        );
      }
      
      const workshopSnapshot = await getDocs(workshopQuery);
      const workshopList = workshopSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        created_at: doc.data().created_at?.toDate() || new Date(),
        estimated_completion: doc.data().estimated_completion?.toDate() || new Date()
      }));
      
      setWorkshopData(workshopList);
      
      // Aktualizacja statystyk pracowni
      setStats(prevStats => ({
        ...prevStats,
        workshopItems: workshopList.length
      }));
    } catch (err) {
      console.error('Błąd podczas pobierania danych pracowni:', err);
    }
  };
  
  // Funkcja do pobierania produktów
  const fetchProductsData = async () => {
    try {
      const productsCollection = collection(db, 'products');
      let productsQuery;
      
      // Filtrowanie według roli i wybranego pracownika
      if (isAdmin && selectedEmployee !== 'all') {
        productsQuery = query(
          productsCollection,
          where('employee_id', '==', selectedEmployee)
        );
      } else if (isAdmin) {
        // Admin widzi wszystkie produkty
        productsQuery = productsCollection;
      } else {
        // Zwykły użytkownik widzi tylko swoje produkty
        productsQuery = query(
          productsCollection,
          where('employee_id', '==', currentUser.uid)
        );
      }
      
      const productsSnapshot = await getDocs(productsQuery);
      
      const productsList = productsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setProductsData(productsList);
    } catch (err) {
      console.error('Błąd podczas pobierania produktów:', err);
    }
  };

  // Funkcja do pobierania danych sprzedaży
  const fetchSalesData = async (startDate, endDate) => {
    try {
      const salesCollection = collection(db, 'sales');
      let salesQuery;
      
      // Filtrowanie według ról i wybranego pracownika
      if (isAdmin && selectedEmployee !== 'all') {
        salesQuery = query(
          salesCollection,
          where('user_id', '==', selectedEmployee),
          where('reported_at', '>=', Timestamp.fromDate(startDate)),
          where('reported_at', '<=', Timestamp.fromDate(endDate))
        );
      } else if (isAdmin) {
        // Admin widzi wszystkie sprzedaże
        salesQuery = query(
          salesCollection,
          where('reported_at', '>=', Timestamp.fromDate(startDate)),
          where('reported_at', '<=', Timestamp.fromDate(endDate))
        );
      } else {
        // Zwykły użytkownik widzi tylko swoje sprzedaże
        salesQuery = query(
          salesCollection,
          where('user_id', '==', currentUser.uid),
          where('reported_at', '>=', Timestamp.fromDate(startDate)),
          where('reported_at', '<=', Timestamp.fromDate(endDate))
        );
      }
      
      const salesSnapshot = await getDocs(salesQuery);
      const salesList = salesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        reported_at: doc.data().reported_at?.toDate() || new Date(),
        sale_date: doc.data().sale_date?.toDate() || new Date(),
        approved_at: doc.data().approved_at?.toDate() || null,
        rejected_at: doc.data().rejected_at?.toDate() || null
      }));
      
      setSalesData(salesList);

      // Dodanie statystyk sprzedaży do ogólnych statystyk
      const approvedSales = salesList.filter(sale => sale.status === 'approved');
      const pendingSales = salesList.filter(sale => sale.status === 'pending');
      const rejectedSales = salesList.filter(sale => sale.status === 'rejected');
      
      const approvedSalesValue = approvedSales.reduce((sum, sale) => sum + (parseFloat(sale.amount) || 0), 0);
      const pendingSalesValue = pendingSales.reduce((sum, sale) => sum + (parseFloat(sale.amount) || 0), 0);
      
      setStats(prevStats => ({
        ...prevStats,
        approvedSalesCount: approvedSales.length,
        pendingSalesCount: pendingSales.length,
        rejectedSalesCount: rejectedSales.length,
        approvedSalesValue,
        pendingSalesValue
      }));
      
    } catch (err) {
      console.error('Błąd podczas pobierania danych sprzedaży:', err);
    }
  };

  useEffect(() => {
    if (currentUser) {
      fetchDashboardData();
      if (isAdmin) {
        fetchEmployees();
      }
    }
  }, [currentUser, selectedMonth, selectedEmployee]);

  // Renderowanie komponentu ładowania
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Renderowanie błędu
  if (error) {
    return (
      <Box>
        <Typography color="error" variant="h6">
          {error}
        </Typography>
      </Box>
    );
  }

  // Rendering komponentu statystyk sprzedaży
  const renderSalesStats = () => {
    return (
      <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <SalesIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="h6">Statystyki sprzedaży</Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ bgcolor: 'success.light' }}>
              <CardContent>
                <Typography variant="body2" color="text.secondary">
                  Zatwierdzone sprzedaże
                </Typography>
                <Typography variant="h5">
                  {stats.approvedSalesCount || 0}
                </Typography>
                <Typography variant="body2" color="success.dark">
                  {stats.approvedSalesValue?.toLocaleString() || 0} PLN
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          {isAdmin && (
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ bgcolor: 'warning.light' }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    Oczekujące sprzedaże
                  </Typography>
                  <Typography variant="h5">
                    {stats.pendingSalesCount || 0}
                  </Typography>
                  <Typography variant="body2" color="warning.dark">
                    {stats.pendingSalesValue?.toLocaleString() || 0} PLN
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
          
          {isAdmin && (
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ bgcolor: 'error.light' }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    Odrzucone sprzedaże
                  </Typography>
                  <Typography variant="h5">
                    {stats.rejectedSalesCount || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Paper>
    );
  };

  // Rendering tabeli sprzedaży
  const renderSalesTable = () => {
    // Filtrujemy sprzedaże na podstawie uprawnień
    let displaySales = [...salesData];
    
    // Dla zwykłych użytkowników pokazujemy wszystkie ich sprzedaże, niezależnie od statusu
    if (!isAdmin) {
      displaySales = displaySales.filter(sale => sale.user_id === currentUser.uid);
    }
    
    // Sortowanie sprzedaży - najpierw oczekujące, potem zatwierdzone, na końcu odrzucone
    displaySales.sort((a, b) => {
      if (a.status === 'pending' && b.status !== 'pending') return -1;
      if (a.status !== 'pending' && b.status === 'pending') return 1;
      if (a.status === 'approved' && b.status === 'rejected') return -1;
      if (a.status === 'rejected' && b.status === 'approved') return 1;
      return new Date(b.sale_date) - new Date(a.sale_date);
    });

    // Funkcja do mapowania statusów na przyjazne nazwy
    const getSaleStatusText = (status) => {
      switch(status) {
        case 'pending': return 'W realizacji';
        case 'approved': return 'Gotowe';
        case 'rejected': return 'Odrzucone';
        default: return status;
      }
    };
    
    // Funkcja do określania koloru statusu
    const getSaleStatusColor = (status) => {
      switch(status) {
        case 'pending': return 'warning';
        case 'approved': return 'success';
        case 'rejected': return 'error';
        default: return 'default';
      }
    };
    
    return (
      <Paper elevation={2} sx={{ p: 2, mt: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Ostatnie sprzedaże</Typography>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Data</TableCell>
                <TableCell>Produkt</TableCell>
                <TableCell>Kwota</TableCell>
                <TableCell>Status</TableCell>
                {isAdmin && <TableCell>Doradca</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {displaySales.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 5 : 4} align="center">
                    Brak danych o sprzedaży w wybranym okresie
                  </TableCell>
                </TableRow>
              ) : (
                displaySales.slice(0, 10).map((sale) => (
                  <TableRow 
                    key={sale.id}
                    sx={{
                      bgcolor: sale.status === 'pending' ? 'rgba(255, 193, 7, 0.1)' : 
                             sale.status === 'rejected' ? 'rgba(244, 67, 54, 0.1)' : 'inherit'
                    }}
                  >
                    <TableCell>
                      {format(sale.sale_date, 'dd.MM.yyyy')}
                    </TableCell>
                    <TableCell>
                      {sale.product_data ? (
                        sale.product_data.type === 'jewelry' ? (
                          <>
                            {sale.product_data.jewelry_name || 'Biżuteria'}
                            <Typography variant="caption" display="block" color="text.secondary">
                              ID: {sale.product_data.sku || ''}
                            </Typography>
                          </>
                        ) : (
                          `${sale.product_data.brand || ''} ${sale.product_data.model || ''}`
                        )
                      ) : (
                        'Brak danych'
                      )}
                    </TableCell>
                    <TableCell>{sale.amount?.toLocaleString()} PLN</TableCell>
                    <TableCell>
                      <Chip 
                        size="small"
                        label={getSaleStatusText(sale.status)}
                        color={getSaleStatusColor(sale.status)}
                      />
                    </TableCell>
                    {isAdmin && (
                      <TableCell>
                        {sale.user_name || sale.user_id}
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  };

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Nagłówek z filtrowaniem */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>
              {isAdmin 
                ? "Dashboard - Przegląd działalności" 
                : "Twój Dashboard - Twoje zamówienia i statystyki"}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                label="Miesiąc"
                type="month"
                value={format(selectedMonth, 'yyyy-MM')}
                onChange={handleMonthChange}
                sx={{ minWidth: 150 }}
                InputLabelProps={{ shrink: true }}
              />
              {isAdmin && (
                <FormControl sx={{ minWidth: 150 }}>
                  <InputLabel>Pracownik</InputLabel>
                  <Select
                    value={selectedEmployee}
                    onChange={handleEmployeeChange}
                    label="Pracownik"
                  >
                    <MenuItem value="all">Wszyscy</MenuItem>
                    {employees.map((employee) => (
                      <MenuItem key={employee.id} value={employee.id}>
                        {employee.firstName} {employee.lastName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <IconButton onClick={handleRefresh}>
                <RefreshIcon />
              </IconButton>
            </Box>
          </Paper>
        </Grid>

        {/* Karty ze statystykami */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" color="text.secondary">Zamówienia</Typography>
                <InventoryIcon color="primary" />
              </Box>
              <Typography variant="h4">{stats.totalOrders}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <Chip size="small" label={`W trakcie: ${stats.pendingOrders}`} sx={{ mr: 1 }} />
                <Chip size="small" label={`Ukończone: ${stats.completedOrders}`} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" color="text.secondary">
                  {isAdmin ? "Zadatki" : "Twoje zadatki"}
                </Typography>
                <MoneyIcon color="primary" />
              </Box>
              <Typography variant="h4">{stats.totalDeposits.toLocaleString()} PLN</Typography>
              <Typography variant="body2" color="text.secondary">
                Bieżący miesiąc
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" color="text.secondary">
                  {isAdmin ? "Sprzedaż" : "Twoja sprzedaż"}
                </Typography>
                <SalesIcon color="success" />
              </Box>
              <Typography variant="h4">{stats.totalSales.toLocaleString()} PLN</Typography>
              <Typography variant="body2" color="text.secondary">
                Bieżący miesiąc
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" color="text.secondary">
                  {isAdmin ? "Aktywne w pracowni" : "Twoje zlecenia w pracowni"}
                </Typography>
                <WorkshopIcon color="warning" />
              </Box>
              <Typography variant="h4">{stats.workshopItems}</Typography>
              <Typography variant="body2" color="text.secondary">
                Aktywne zlecenia
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Zakładki z różnymi widokami */}
        <Grid item xs={12}>
          <Paper sx={{ mb: 2 }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange} 
              variant="fullWidth" 
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label="Zamówienia" />
              <Tab label="Pracownia" />
              <Tab label="Produkty" />
              <Tab label="Sprzedaż" />
            </Tabs>
            <Box p={3}>
              {tabValue === 0 && (
                <Grid container spacing={3}>
                  {/* Wykres statusu zamówień */}
                  <Grid item xs={12} md={6}>
                    <Card>
                      <Paper elevation={3} sx={{ height: '100%', p: 0 }}>
                        <Box p={2}>
                          <Typography variant="h5" component="h1" gutterBottom>
                            {isAdmin ? 'Panel statystyk - wszystkie dane' : 'Twój panel statystyk'}
                          </Typography>
                          {!isAdmin && (
                            <Box sx={{ mb: 2, color: 'text.secondary', fontSize: '0.95rem' }}>
                              Twoje zamówienia, zadatki i aktywne zlecenia w pracowni
                            </Box>
                          )}
                          {ordersData.length > 0 ? (
                            <Box height={250}>
                              <Pie 
                                data={{
                                  labels: ['Nowe', 'W realizacji', 'Gotowe', 'Anulowane'],
                                  datasets: [{
                                    data: [
                                      ordersData.filter(order => order.status === 'new').length,
                                      ordersData.filter(order => order.status === 'in_progress').length,
                                      ordersData.filter(order => order.status === 'ready').length,
                                      ordersData.filter(order => order.status === 'cancelled').length
                                    ],
                                    backgroundColor: [
                                      '#2196f3', // Niebieskie - nowe
                                      '#ff9800', // Pomarańczowe - w realizacji
                                      '#4caf50', // Zielone - gotowe
                                      '#f44336'  // Czerwone - anulowane
                                    ],
                                    borderWidth: 1
                                  }]
                                }}
                                options={{
                                  responsive: true,
                                  maintainAspectRatio: false,
                                }}
                              />
                            </Box>
                          ) : (
                            <Typography align="center">
                              Brak danych o zamówieniach w wybranym okresie
                            </Typography>
                          )}
                        </Box>
                      </Paper>
                    </Card>
                  </Grid>
                  
                  {/* Wykres zamówień według lokalizacji */}
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Zamówienia według lokalizacji
                        </Typography>
                        {ordersData.length > 0 ? (
                          <Box height={250}>
                            <Bar
                              data={{
                                labels: ['PAN', 'VVS', 'MODO', 'KLIF', 'GP', 'SB', 'MB'],
                                datasets: [{
                                  label: 'Liczba zamówień',
                                  data: [
                                    ordersData.filter(order => order.location === 'PAN').length,
                                    ordersData.filter(order => order.location === 'VVS').length,
                                    ordersData.filter(order => order.location === 'MODO').length,
                                    ordersData.filter(order => order.location === 'KLIF').length,
                                    ordersData.filter(order => order.location === 'GP').length,
                                    ordersData.filter(order => order.location === 'SB').length,
                                    ordersData.filter(order => order.location === 'MB').length
                                  ],
                                  backgroundColor: 'rgba(212, 175, 55, 0.7)',
                                  borderColor: 'rgba(212, 175, 55, 1)',
                                  borderWidth: 1
                                }]
                              }}
                              options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                scales: {
                                  y: {
                                    beginAtZero: true,
                                    ticks: {
                                      precision: 0
                                    }
                                  }
                                }
                              }}
                            />
                          </Box>
                        ) : (
                          <Typography align="center">
                            Brak danych o zamówieniach w wybranym okresie
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  {/* Tabela najnowszych zamówień */}
                  <Grid item xs={12}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Najnowsze zamówienia
                        </Typography>
                        {ordersData.length > 0 ? (
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Klient</TableCell>
                                  <TableCell>Produkt</TableCell>
                                  <TableCell>Data</TableCell>
                                  <TableCell>Kwota</TableCell>
                                  <TableCell>Status</TableCell>
                                  {isAdmin && <TableCell>Pracownik</TableCell>}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {ordersData
                                  .sort((a, b) => b.created_at - a.created_at)
                                  .slice(0, 5)
                                  .map((order) => (
                                    <TableRow key={order.id}>
                                      <TableCell>{order.client_name}</TableCell>
                                      <TableCell>{order.product_description}</TableCell>
                                      <TableCell>{format(order.created_at, 'dd.MM.yyyy', { locale: pl })}</TableCell>
                                      <TableCell>{parseFloat(order.total_amount || 0).toLocaleString()} PLN</TableCell>
                                      <TableCell>
                                        <Chip 
                                          size="small" 
                                          label={{
                                            'new': 'Nowe',
                                            'in_progress': 'W realizacji',
                                            'ready': 'Gotowe',
                                            'cancelled': 'Anulowane'
                                          }[order.status] || 'Nieznany'}
                                          color={{
                                            'new': 'info',
                                            'in_progress': 'warning',
                                            'ready': 'success',
                                            'cancelled': 'error'
                                          }[order.status] || 'default'}
                                        />
                                      </TableCell>
                                      {isAdmin && <TableCell>{order.employee_name || 'Nieprzypisany'}</TableCell>}
                                    </TableRow>
                                  ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        ) : (
                          <Typography align="center">
                            Brak danych o zamówieniach w wybranym okresie
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}
              
              {tabValue === 1 && (
                <Grid container spacing={3}>
                  {/* Wykres statusów w pracowni */}
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Status zleceń w pracowni
                        </Typography>
                        {workshopData.length > 0 ? (
                          <Box height={250}>
                            <Pie 
                              data={{
                                labels: ['Oczekujące', 'W realizacji'],
                                datasets: [{
                                  data: [
                                    workshopData.filter(item => item.status === 'waiting').length,
                                    workshopData.filter(item => item.status === 'in_progress').length
                                  ],
                                  backgroundColor: [
                                    '#9c27b0', // Fioletowy - oczekujące
                                    '#ff9800'  // Pomarańczowy - w realizacji
                                  ],
                                  borderWidth: 1
                                }]
                              }}
                              options={{
                                responsive: true,
                                maintainAspectRatio: false,
                              }}
                            />
                          </Box>
                        ) : (
                          <Typography align="center">
                            Brak aktywnych zleceń w pracowni
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  {/* Wykres zleceń według typów */}
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Zlecenia według typów
                        </Typography>
                        {workshopData.length > 0 ? (
                          <Box height={250}>
                            <Bar
                              data={{
                                labels: ['Naprawa', 'Grawerowanie', 'Przeróbka', 'Czyszczenie', 'Inne'],
                                datasets: [{
                                  label: 'Liczba zleceń',
                                  data: [
                                    workshopData.filter(item => item.job_type === 'repair').length,
                                    workshopData.filter(item => item.job_type === 'engraving').length,
                                    workshopData.filter(item => item.job_type === 'modification').length,
                                    workshopData.filter(item => item.job_type === 'cleaning').length,
                                    workshopData.filter(item => item.job_type === 'other').length,
                                  ],
                                  backgroundColor: 'rgba(126, 87, 194, 0.7)',
                                  borderColor: 'rgba(126, 87, 194, 1)',
                                  borderWidth: 1
                                }]
                              }}
                              options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                scales: {
                                  y: {
                                    beginAtZero: true,
                                    ticks: {
                                      precision: 0
                                    }
                                  }
                                }
                              }}
                            />
                          </Box>
                        ) : (
                          <Typography align="center">
                            Brak aktywnych zleceń w pracowni
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  {/* Lista aktywnych zleceń w pracowni */}
                  <Grid item xs={12}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Aktywne zlecenia w pracowni
                        </Typography>
                        {workshopData.length > 0 ? (
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Produkt</TableCell>
                                  <TableCell>Typ zlecenia</TableCell>
                                  <TableCell>Status</TableCell>
                                  <TableCell>Data przyjęcia</TableCell>
                                  <TableCell>Planowane zakończenie</TableCell>
                                  {isAdmin && <TableCell>Pracownik</TableCell>}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {workshopData
                                  .sort((a, b) => a.estimated_completion - b.estimated_completion)
                                  .slice(0, 5)
                                  .map((item) => (
                                    <TableRow key={item.id}>
                                      <TableCell>{item.product_name || 'Bez nazwy'}</TableCell>
                                      <TableCell>
                                        {{
                                          'repair': 'Naprawa',
                                          'engraving': 'Grawerowanie',
                                          'modification': 'Przeróbka',
                                          'cleaning': 'Czyszczenie',
                                          'other': 'Inne'
                                        }[item.job_type] || 'Nieznany'}
                                      </TableCell>
                                      <TableCell>
                                        <Chip 
                                          size="small" 
                                          label={{
                                            'waiting': 'Oczekuje',
                                            'in_progress': 'W realizacji'
                                          }[item.status] || 'Nieznany'}
                                          color={{
                                            'waiting': 'secondary',
                                            'in_progress': 'warning'
                                          }[item.status] || 'default'}
                                        />
                                      </TableCell>
                                      <TableCell>{format(item.created_at, 'dd.MM.yyyy', { locale: pl })}</TableCell>
                                      <TableCell>{format(item.estimated_completion, 'dd.MM.yyyy', { locale: pl })}</TableCell>
                                      {isAdmin && <TableCell>{item.employee_name || 'Nieprzypisany'}</TableCell>}
                                    </TableRow>
                                  ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        ) : (
                          <Typography align="center">
                            Brak aktywnych zleceń w pracowni
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}
              
              {tabValue === 2 && (
                <Grid container spacing={3}>
                  {/* Wykres statusu produktów */}
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Status produktów
                        </Typography>
                        {productsData.length > 0 ? (
                          <Box height={250}>
                            <Pie 
                              data={{
                                labels: ['Dostępne', 'Zarezerwowane', 'Sprzedane', 'W pracowni', 'Wysłane'],
                                datasets: [{
                                  data: [
                                    productsData.filter(product => product.status === 'available').length,
                                    productsData.filter(product => product.status === 'reserved').length,
                                    productsData.filter(product => product.status === 'sold').length,
                                    productsData.filter(product => product.status === 'in_workshop').length,
                                    productsData.filter(product => product.status === 'shipped').length
                                  ],
                                  backgroundColor: [
                                    '#4caf50', // Zielone - dostępne
                                    '#ff9800', // Pomarańczowe - zarezerwowane
                                    '#2196f3', // Niebieskie - sprzedane
                                    '#9c27b0', // Fioletowe - w pracowni
                                    '#795548'  // Brązowe - wysłane
                                  ],
                                  borderWidth: 1
                                }]
                              }}
                              options={{
                                responsive: true,
                                maintainAspectRatio: false,
                              }}
                            />
                          </Box>
                        ) : (
                          <Typography align="center">
                            Brak danych o produktach
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  {/* Wykres produktów według lokalizacji */}
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Produkty według lokalizacji
                        </Typography>
                        {productsData.length > 0 ? (
                          <Box height={250}>
                            <Bar
                              data={{
                                labels: ['PAN', 'VVS', 'MODO', 'KLIF', 'GP', 'SB', 'MB'],
                                datasets: [{
                                  label: 'Liczba produktów',
                                  data: [
                                    productsData.filter(product => product.location === 'PAN').length,
                                    productsData.filter(product => product.location === 'VVS').length,
                                    productsData.filter(product => product.location === 'MODO').length,
                                    productsData.filter(product => product.location === 'KLIF').length,
                                    productsData.filter(product => product.location === 'GP').length,
                                    productsData.filter(product => product.location === 'SB').length,
                                    productsData.filter(product => product.location === 'MB').length
                                  ],
                                  backgroundColor: 'rgba(76, 175, 80, 0.7)',
                                  borderColor: 'rgba(76, 175, 80, 1)',
                                  borderWidth: 1
                                }]
                              }}
                              options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                scales: {
                                  y: {
                                    beginAtZero: true,
                                    ticks: {
                                      precision: 0
                                    }
                                  }
                                }
                              }}
                            />
                          </Box>
                        ) : (
                          <Typography align="center">
                            Brak danych o produktach
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  {/* Produkty według kategorii */}
                  <Grid item xs={12}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Produkty według kategorii
                        </Typography>
                        {productsData.length > 0 ? (
                          <Box height={250}>
                            <Line
                              data={{
                                labels: ['Pierścionki', 'Naszyjniki', 'Bransoletki', 'Kolczyki', 'Zegarki', 'Inne'],
                                datasets: [
                                  {
                                    label: 'Dostępne',
                                    data: [
                                      productsData.filter(p => p.category === 'ring' && p.status === 'available').length,
                                      productsData.filter(p => p.category === 'necklace' && p.status === 'available').length,
                                      productsData.filter(p => p.category === 'bracelet' && p.status === 'available').length,
                                      productsData.filter(p => p.category === 'earrings' && p.status === 'available').length,
                                      productsData.filter(p => p.category === 'watch' && p.status === 'available').length,
                                      productsData.filter(p => p.category === 'other' && p.status === 'available').length,
                                    ],
                                    borderColor: '#4caf50',
                                    backgroundColor: 'rgba(76, 175, 80, 0.2)',
                                    tension: 0.4,
                                    fill: true
                                  },
                                  {
                                    label: 'Sprzedane',
                                    data: [
                                      productsData.filter(p => p.category === 'ring' && p.status === 'sold').length,
                                      productsData.filter(p => p.category === 'necklace' && p.status === 'sold').length,
                                      productsData.filter(p => p.category === 'bracelet' && p.status === 'sold').length,
                                      productsData.filter(p => p.category === 'earrings' && p.status === 'sold').length,
                                      productsData.filter(p => p.category === 'watch' && p.status === 'sold').length,
                                      productsData.filter(p => p.category === 'other' && p.status === 'sold').length,
                                    ],
                                    borderColor: '#2196f3',
                                    backgroundColor: 'rgba(33, 150, 243, 0.2)',
                                    tension: 0.4,
                                    fill: true
                                  }
                                ]
                              }}
                              options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                scales: {
                                  y: {
                                    beginAtZero: true,
                                    ticks: {
                                      precision: 0
                                    }
                                  }
                                }
                              }}
                            />
                          </Box>
                        ) : (
                          <Typography align="center">
                            Brak danych o produktach
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}
              
              {tabValue === 3 && (
                <Grid container spacing={3}>
                  {renderSalesStats()}
                  {renderSalesTable()}
                </Grid>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
